import { type ChannelType, channelTypeSchema } from '@discordo/shared';
import { useState, type FormEvent, type ReactElement, type ReactNode } from 'react';
import type { ZodType } from 'zod';

import { ApiError } from '../lib/api';

/**
 * The three dialogs the sidebar puts up to manage channels and categories.
 *
 * They are here, and not inside `ChannelSidebar`, for one reason: the sidebar
 * is a list of things you click, and a dialog is a thing that asks a question
 * and reports what the API answered. Keeping the asking apart is what lets the
 * sidebar stay readable while six different actions share the same two shapes
 * — ask for a text, or ask for a confirmation.
 *
 * The validation each one runs is the very schema the API parses the body
 * with, imported from `@discordo/shared`. A name that could never be accepted
 * therefore never leaves the browser, and the wording of the refusal is the
 * same on both sides because it has a single author.
 */

const GENERIC_FAILURE = 'Não foi possível concluir. Tente de novo.';

/** What the interface says when the API refused for a reason it stated. */
function describe(cause: unknown): string {
  return cause instanceof ApiError ? cause.message : GENERIC_FAILURE;
}

interface DialogFrameProps {
  title: string;
  hint?: string | undefined;
  error: string | null;
  submitLabel: string;
  submittingLabel: string;
  submitting: boolean;
  destructive?: boolean;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onClose: () => void;
  children?: ReactNode;
}

/** The shell every dialog below wears: title, error, body, and two buttons. */
function DialogFrame({
  title,
  hint,
  error,
  submitLabel,
  submittingLabel,
  submitting,
  destructive = false,
  onSubmit,
  onClose,
  children,
}: DialogFrameProps): ReactElement {
  return (
    <div className="guild-dialog-backdrop">
      <form className="guild-dialog" noValidate onSubmit={onSubmit}>
        <h2 className="guild-dialog-title">{title}</h2>
        {hint !== undefined && <p className="guild-dialog-hint">{hint}</p>}

        {error !== null && (
          <p className="auth-alert" role="alert">
            {error}
          </p>
        )}

        {children}

        <div className="guild-dialog-actions">
          <button type="button" className="guild-dialog-cancel" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="submit"
            className={destructive ? 'auth-submit is-destructive' : 'auth-submit'}
            disabled={submitting}
          >
            {submitting ? submittingLabel : submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Asking for a text
// ---------------------------------------------------------------------------

export interface PromptDialogProps {
  title: string;
  hint?: string | undefined;
  /** Label of the single field, and its accessible name. */
  label: string;
  initialValue: string;
  submitLabel: string;
  /** Shown while the request is in flight; defaults to saving. */
  submittingLabel?: string;
  /** The schema the API validates the same field with. */
  schema: ZodType<string>;
  onSubmit: (value: string) => Promise<void>;
  onClose: () => void;
}

/**
 * Asks for one text and hands the parsed value over.
 *
 * A refusal keeps the dialog open with what was typed still in the field: the
 * name someone chose is theirs, and losing it to an error is a second cost on
 * top of the first.
 */
export function PromptDialog({
  title,
  hint,
  label,
  initialValue,
  submitLabel,
  submittingLabel = 'Salvando…',
  schema,
  onSubmit,
  onClose,
}: PromptDialogProps): ReactElement {
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);

    const parsed = schema.safeParse(value);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Dados inválidos.');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(parsed.data);
      onClose();
    } catch (cause) {
      setError(describe(cause));
      setSubmitting(false);
    }
  }

  return (
    <DialogFrame
      title={title}
      hint={hint}
      error={error}
      submitLabel={submitLabel}
      submittingLabel={submittingLabel}
      submitting={submitting}
      onSubmit={(event) => {
        void handleSubmit(event);
      }}
      onClose={onClose}
    >
      <div className="auth-field">
        <label htmlFor="channel-dialog-value">{label}</label>
        <input
          id="channel-dialog-value"
          name="value"
          autoFocus
          value={value}
          onChange={(event) => setValue(event.target.value)}
        />
      </div>
    </DialogFrame>
  );
}

// ---------------------------------------------------------------------------
// Asking for a confirmation
// ---------------------------------------------------------------------------

export interface ConfirmDialogProps {
  title: string;
  /** What is about to be lost, said plainly. */
  message: string;
  confirmLabel: string;
  /** Shown while the request is in flight; defaults to deleting. */
  submittingLabel?: string;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}

export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  submittingLabel = 'Apagando…',
  onConfirm,
  onClose,
}: ConfirmDialogProps): ReactElement {
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await onConfirm();
      onClose();
    } catch (cause) {
      setError(describe(cause));
      setSubmitting(false);
    }
  }

  return (
    <DialogFrame
      title={title}
      hint={message}
      error={error}
      submitLabel={confirmLabel}
      submittingLabel={submittingLabel}
      submitting={submitting}
      destructive
      onSubmit={(event) => {
        void handleSubmit(event);
      }}
      onClose={onClose}
    />
  );
}

// ---------------------------------------------------------------------------
// Opening a channel
// ---------------------------------------------------------------------------

export interface CreateChannelDialogProps {
  /** The section the `+` was clicked in; the type starts there. */
  initialType: ChannelType;
  schema: ZodType<string>;
  onCreate: (input: { name: string; type: ChannelType }) => Promise<void>;
  onClose: () => void;
}

const TYPE_LABELS: Record<ChannelType, string> = {
  text: 'Texto',
  voice: 'Voz',
};

/**
 * Asks for the name and the kind.
 *
 * The kind is asked even though the `+` already came from a section, because
 * the section is where you happened to be looking and not necessarily what you
 * meant: opening the dialog from the text section and realising you wanted a
 * voice room should not cost a cancel and a second click.
 */
export function CreateChannelDialog({
  initialType,
  schema,
  onCreate,
  onClose,
}: CreateChannelDialogProps): ReactElement {
  const [name, setName] = useState('');
  const [type, setType] = useState<ChannelType>(initialType);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);

    const parsed = schema.safeParse(name);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Dados inválidos.');
      return;
    }

    setSubmitting(true);
    try {
      await onCreate({ name: parsed.data, type });
      onClose();
    } catch (cause) {
      setError(describe(cause));
      setSubmitting(false);
    }
  }

  return (
    <DialogFrame
      title="Criar canal"
      hint="Canais de texto guardam conversa; canais de voz são salas separadas — quem está em um não ouve quem está em outro."
      error={error}
      submitLabel="Criar"
      submittingLabel="Criando…"
      submitting={submitting}
      onSubmit={(event) => {
        void handleSubmit(event);
      }}
      onClose={onClose}
    >
      <div className="auth-field">
        <label htmlFor="channel-dialog-value">Nome do canal</label>
        <input
          id="channel-dialog-value"
          name="name"
          autoFocus
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </div>

      <div className="auth-field">
        <label htmlFor="channel-dialog-type">Tipo</label>
        <select
          id="channel-dialog-type"
          name="type"
          value={type}
          onChange={(event) => {
            // The value comes from the same enum the API parses, so an option
            // the server would refuse cannot be selected here either.
            const parsed = channelTypeSchema.safeParse(event.target.value);
            if (parsed.success) {
              setType(parsed.data);
            }
          }}
        >
          {channelTypeSchema.options.map((option) => (
            <option key={option} value={option}>
              {TYPE_LABELS[option]}
            </option>
          ))}
        </select>
      </div>
    </DialogFrame>
  );
}
